import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, X, ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";

const WEEKS = 6;

function mondayOf(date = new Date()) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Mon = 0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function iso(d) {
  // Local-date ISO (YYYY-MM-DD) — avoid toISOString() which shifts by UTC offset
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shortLabel(d) {
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function Planning() {
  const { user, activeWorkspace } = useAuth();
  const { toast } = useToast();
  const [anchor, setAnchor] = useState(() => addDays(mondayOf(), -7)); // show prev week + 5 upcoming
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [allocs, setAllocs] = useState([]);
  const [entries, setEntries] = useState([]);
  const [rows, setRows] = useState([]); // [{ user_id, project_id }]
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ user_id: "", project_id: "" });
  const [capForOpen, setCapForOpen] = useState(null); // member
  const [capValue, setCapValue] = useState(40);

  const weeks = useMemo(() => Array.from({ length: WEEKS }, (_, i) => addDays(anchor, i * 7)), [anchor]);
  const fromIso = iso(weeks[0]);
  const toIso = iso(weeks[weeks.length - 1]);
  const entriesToIso = iso(addDays(weeks[weeks.length - 1], 6)); // Sunday of last week

  const load = useCallback(async () => {
    if (!activeWorkspace) return;
    const [m, p, c, a, e] = await Promise.all([
      api.members(activeWorkspace.id),
      api.projects(),
      api.clients(),
      api.allocations({ date_from: fromIso, date_to: toIso }),
      api.entries({ date_from: fromIso, date_to: entriesToIso }),
    ]);
    setMembers(m); setProjects(p); setClients(c); setAllocs(a); setEntries(e);

    // Row set = existing (user, project) combos in this window from allocs OR entries
    const combos = new Map();
    a.forEach((x) => combos.set(`${x.user_id}::${x.project_id}`, { user_id: x.user_id, project_id: x.project_id }));
    e.forEach((x) => combos.set(`${x.user_id}::${x.project_id}`, { user_id: x.user_id, project_id: x.project_id }));
    if (combos.size === 0 && m.length && p.length) {
      combos.set(`${m[0].user_id}::${p[0].id}`, { user_id: m[0].user_id, project_id: p[0].id });
    }
    setRows(Array.from(combos.values()));
  }, [activeWorkspace, fromIso, toIso, entriesToIso]);

  useEffect(() => { load(); }, [load]);

  const myRole = members.find((mm) => mm.user_id === user?.id)?.role;
  const canEditCapacity = myRole === "owner" || myRole === "admin" || activeWorkspace?.is_platform_access;

  const memberById = Object.fromEntries(members.map((m) => [m.user_id, m]));
  const projectById = Object.fromEntries(projects.map((p) => [p.id, p]));
  const clientById = Object.fromEntries(clients.map((c) => [c.id, c]));

  const allocFor = (uid, pid, weekIso) =>
    allocs.find((a) => a.user_id === uid && a.project_id === pid && a.week_start === weekIso);

  // Week bucket for an entry's date (Monday, YYYY-MM-DD)
  const weekOfEntry = useCallback((dateStr) => iso(mondayOf(new Date(dateStr))), []);

  // Aggregate actual hours from time entries -> Map<`${uid}::${pid}::${weekIso}`, hours>
  const actualMap = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const wk = weekOfEntry(e.date);
      const key = `${e.user_id}::${e.project_id}::${wk}`;
      map.set(key, (map.get(key) || 0) + e.hours);
    });
    return map;
  }, [entries, weekOfEntry]);

  const actualFor = (uid, pid, weekIso) => actualMap.get(`${uid}::${pid}::${weekIso}`) || 0;

  const actualForMemberWeek = (uid, weekIso) => {
    let total = 0;
    for (const [key, hours] of actualMap) {
      const [u, , w] = key.split("::");
      if (u === uid && w === weekIso) total += hours;
    }
    return total;
  };

  const actualForProjectWeek = (pid, weekIso) => {
    let total = 0;
    for (const [key, hours] of actualMap) {
      const [, p, w] = key.split("::");
      if (p === pid && w === weekIso) total += hours;
    }
    return total;
  };

  const actualForWeek = (weekIso) => {
    let total = 0;
    for (const [key, hours] of actualMap) {
      if (key.endsWith("::" + weekIso)) total += hours;
    }
    return total;
  };

  const totalForMemberWeek = (uid, weekIso) =>
    allocs.filter((a) => a.user_id === uid && a.week_start === weekIso).reduce((sum, a) => sum + a.hours, 0);

  const totalForWeek = (weekIso) =>
    allocs.filter((a) => a.week_start === weekIso).reduce((sum, a) => sum + a.hours, 0);

  const setCell = async (row, weekIso, rawValue) => {
    const hours = Math.max(0, Number(rawValue) || 0);
    try {
      const updated = await api.upsertAllocation({
        user_id: row.user_id, project_id: row.project_id,
        week_start: weekIso, hours, notes: "",
      });
      // Reconcile local state
      setAllocs((prev) => {
        const filtered = prev.filter((a) =>
          !(a.user_id === row.user_id && a.project_id === row.project_id && a.week_start === weekIso)
        );
        return hours > 0 ? [...filtered, updated] : filtered;
      });
    } catch (ex) {
      toast({ title: "Couldn't save allocation", description: apiErrorMessage(ex, "") });
    }
  };

  const addRow = (e) => {
    e.preventDefault();
    if (!addForm.user_id || !addForm.project_id) return;
    const key = `${addForm.user_id}::${addForm.project_id}`;
    if (rows.some((r) => `${r.user_id}::${r.project_id}` === key)) {
      toast({ title: "Row already exists" });
    } else {
      setRows([...rows, { user_id: addForm.user_id, project_id: addForm.project_id }]);
    }
    setAddForm({ user_id: "", project_id: "" });
    setAddOpen(false);
  };

  const removeRow = (row) => {
    // Only remove rows that have no allocations across the visible window
    const hasData = allocs.some((a) => a.user_id === row.user_id && a.project_id === row.project_id);
    if (hasData) {
      toast({ title: "Clear hours first", description: "Row has planned hours in view. Set them to 0 to remove." });
      return;
    }
    setRows(rows.filter((r) => !(r.user_id === row.user_id && r.project_id === row.project_id)));
  };

  const openCapacity = (m) => {
    setCapForOpen(m);
    setCapValue(m.capacity_hours ?? 40);
  };
  const saveCapacity = async (e) => {
    e.preventDefault();
    try {
      const updated = await api.patchMember(activeWorkspace.id, capForOpen.user_id, {
        capacity_hours: Math.max(0, Number(capValue) || 0),
      });
      setMembers(members.map((m) => (m.user_id === updated.user_id ? updated : m)));
      setCapForOpen(null);
      toast({ title: "Capacity updated" });
    } catch (ex) {
      toast({ title: "Couldn't update capacity", description: apiErrorMessage(ex, "") });
    }
  };

  const capColor = (planned, capacity) => {
    if (!capacity) return "#22C55E";
    const pct = planned / capacity;
    if (pct > 1) return "#DC2626";      // red
    if (pct > 0.9) return "#F97316";    // orange
    if (pct > 0) return "#22C55E";      // green
    return "#94A3B8";                   // grey (idle)
  };

  // Slippage color: compare actual to planned for a cell
  const slipColor = (actual, planned) => {
    if (!actual && !planned) return null;                 // nothing yet
    if (actual === 0 && planned > 0) return "#94A3B8";    // planned, not started
    if (planned === 0 && actual > 0) return "#DC2626";    // unplanned work
    const ratio = actual / planned;
    if (ratio > 1.15) return "#DC2626";                   // over by >15%
    if (ratio > 1.0) return "#F97316";                    // over
    if (ratio >= 0.9) return "#22C55E";                   // on track
    return "#0EA5E9";                                     // under-tracked so far
  };

  const todayWeekIso = iso(mondayOf());
  const isCurrentOrPastWeek = (weekIso) => weekIso <= todayWeekIso;

  // Members sorted by name for stable render; distinct member rows shown together
  const membersInRows = [...new Set(rows.map((r) => r.user_id))]
    .map((uid) => memberById[uid]).filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-[#6a736f]">Resource planning</div>
          <h1 className="font-display text-4xl mt-1">Who's on the bench next week?</h1>
          <p className="text-sm text-[#3f4a44] mt-2 max-w-2xl">Plan hours per person, per project, per week. Tracked hours appear underneath each cell for weeks that have started — spot slippage before it becomes a problem.</p>
          <div className="mt-3 flex items-center gap-4 text-[11px] text-[#3f4a44] flex-wrap">
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{background:"#22C55E"}}/> on track</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{background:"#0EA5E9"}}/> under-tracked</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{background:"#F97316"}}/> over plan</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{background:"#DC2626"}}/> slipping / unplanned</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAnchor(addDays(anchor, -7 * WEEKS))} className="btn-outline-forest p-2 rounded-md"><ChevronLeft size={16} /></button>
          <div className="text-sm text-[#3f4a44] px-2">{shortLabel(weeks[0])} – {shortLabel(addDays(weeks[weeks.length - 1], 6))}</div>
          <button onClick={() => setAnchor(addDays(anchor, 7 * WEEKS))} className="btn-outline-forest p-2 rounded-md"><ChevronRight size={16} /></button>
          <button onClick={() => setAnchor(addDays(mondayOf(), -7))} className="btn-outline-forest px-3 py-2 rounded-md text-xs font-medium ml-1">Today</button>
          <button onClick={() => setAddOpen(true)} className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ml-2">
            <Plus size={16} /> Add row
          </button>
        </div>
      </div>

      {members.length === 0 || projects.length === 0 ? (
        <div className="mt-6 bg-white rounded-xl border border-cream p-8 text-center">
          <div className="font-display text-2xl">Set the table first</div>
          <p className="text-sm text-[#3f4a44] mt-2">You need at least one team member and one project before you can plan. Invite teammates on the Team page and create a project on the Projects page.</p>
        </div>
      ) : (
        <>
          {/* Capacity summary strip */}
          <div className="mt-6 bg-white rounded-xl border border-cream overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
                <tr className="text-left">
                  <th className="px-4 py-3">Team member</th>
                  <th className="px-3 py-3 text-right">Capacity</th>
                  {weeks.map((d) => (
                    <th key={iso(d)} className="px-2 py-3 text-center">
                      <div>{shortLabel(d)}</div>
                      <div className="text-[10px] text-[#6a736f] font-normal">wk {getWeekNumber(d)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EAF3EE]">
                {members.map((m) => (
                  <tr key={m.user_id}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-[#6a736f]">{m.email}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => canEditCapacity && openCapacity(m)}
                        className={`inline-flex items-center gap-1 text-sm ${canEditCapacity ? "hover:text-coral" : "cursor-default"}`}
                        title={canEditCapacity ? "Edit capacity" : ""}
                      >
                        <span className="tabular-nums">{m.capacity_hours ?? 40}h</span>
                        {canEditCapacity && <Pencil size={12} className="text-[#6a736f]" />}
                      </button>
                    </td>
                    {weeks.map((d) => {
                      const wk = iso(d);
                      const planned = totalForMemberWeek(m.user_id, wk);
                      const actual = actualForMemberWeek(m.user_id, wk);
                      const cap = m.capacity_hours ?? 40;
                      const color = capColor(planned, cap);
                      const showActual = isCurrentOrPastWeek(wk) && (actual > 0 || planned > 0);
                      const slip = showActual ? slipColor(actual, planned) : null;
                      return (
                        <td key={wk} className="px-2 py-2 text-center" data-testid={`cap-${m.user_id}-${wk}`}>
                          <div className="inline-flex items-center gap-1 text-sm">
                            <span className="h-2 w-2 rounded-full" style={{ background: color }} />
                            <span className="tabular-nums">{planned ? planned.toFixed(1) : "—"}</span>
                          </div>
                          <div className="text-[10px] text-[#6a736f]">of {cap}</div>
                          {showActual && (
                            <div
                              className="text-[10px] mt-0.5 inline-flex items-center gap-1 tabular-nums"
                              style={{ color: slip || "#6a736f" }}
                              data-testid={`cap-actual-${m.user_id}-${wk}`}
                              title={`Tracked ${actual.toFixed(1)}h vs planned ${planned.toFixed(1)}h`}
                            >
                              <span className="h-1.5 w-1.5 rounded-full" style={{ background: slip || "#94A3B8" }} />
                              {actual.toFixed(1)}h tracked
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Editable allocation grid, grouped by member */}
          <div className="mt-6 bg-white rounded-xl border border-cream overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
                <tr className="text-left">
                  <th className="px-4 py-3 w-64">Allocation</th>
                  {weeks.map((d) => (
                    <th key={iso(d)} className="px-2 py-3 text-center">{shortLabel(d)}</th>
                  ))}
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {membersInRows.length === 0 ? (
                  <tr><td colSpan={WEEKS + 2} className="px-4 py-8 text-center text-sm text-[#6a736f]">No rows yet. Click <span className="text-coral font-medium">Add row</span> to plan someone's week.</td></tr>
                ) : membersInRows.map((m) => {
                  const memberRows = rows.filter((r) => r.user_id === m.user_id);
                  return (
                    <React.Fragment key={m.user_id}>
                      <tr className="bg-cream-2/40">
                        <td className="px-4 py-2 font-medium text-forest">{m.name}</td>
                        <td colSpan={WEEKS + 1}></td>
                      </tr>
                      {memberRows.map((row) => {
                        const proj = projectById[row.project_id];
                        const client = proj ? clientById[proj.client_id] : null;
                        return (
                          <tr key={`${row.user_id}::${row.project_id}`} className="border-t border-cream">
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full" style={{ background: client?.color || "#94A3B8" }} />
                                <div>
                                  <div className="text-sm">{proj?.name || "Unknown project"}</div>
                                  <div className="text-[11px] text-[#6a736f]">{client?.name || "—"}</div>
                                </div>
                              </div>
                            </td>
                            {weeks.map((d) => {
                              const wk = iso(d);
                              const a = allocFor(row.user_id, row.project_id, wk);
                              const planned = a?.hours ?? 0;
                              const actual = actualFor(row.user_id, row.project_id, wk);
                              const showActual = isCurrentOrPastWeek(wk) && (actual > 0 || planned > 0);
                              const slip = showActual ? slipColor(actual, planned) : null;
                              return (
                                <td key={wk} className="px-1 py-1.5 text-center align-top">
                                  <input
                                    type="number" min={0} step="any"
                                    defaultValue={a ? a.hours : ""}
                                    onBlur={(e) => {
                                      const newV = e.target.value === "" ? 0 : Number(e.target.value);
                                      const oldV = a?.hours ?? 0;
                                      if (newV !== oldV) setCell(row, wk, newV);
                                    }}
                                    className="w-16 text-center border border-cream rounded px-1 py-1 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-coral/40"
                                    placeholder="—"
                                  />
                                  {showActual && (
                                    <div
                                      className="text-[10px] mt-1 tabular-nums inline-flex items-center gap-1"
                                      style={{ color: slip || "#6a736f" }}
                                      data-testid={`cell-actual-${row.user_id}-${row.project_id}-${wk}`}
                                      title={`Tracked ${actual.toFixed(1)}h vs planned ${planned.toFixed(1)}h`}
                                    >
                                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: slip || "#94A3B8" }} />
                                      {actual.toFixed(1)}h
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                            <td className="pr-2">
                              <button onClick={() => removeRow(row)} className="text-[#6a736f] hover:text-coral p-1"><X size={14} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {/* Weekly totals */}
                <tr className="bg-cream-2/60 border-t border-cream">
                  <td className="px-4 py-2 font-semibold text-sm align-top">
                    <div>Total planned</div>
                    <div className="text-[10px] text-[#6a736f] font-normal mt-1">tracked</div>
                  </td>
                  {weeks.map((d) => {
                    const wk = iso(d);
                    const t = totalForWeek(wk);
                    const a = actualForWeek(wk);
                    const show = isCurrentOrPastWeek(wk) && (a > 0 || t > 0);
                    const slip = show ? slipColor(a, t) : null;
                    return (
                      <td key={wk} className="px-2 py-2 text-center tabular-nums font-semibold text-sm align-top">
                        <div>{t ? t.toFixed(1) : "—"}</div>
                        {show && (
                          <div
                            className="text-[10px] font-normal mt-1"
                            style={{ color: slip || "#6a736f" }}
                            data-testid={`total-actual-${wk}`}
                          >
                            {a.toFixed(1)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Per-project weekly load — plan vs actual */}
          <div className="mt-6 bg-white rounded-xl border border-cream p-5">
            <div className="flex items-center justify-between">
              <div className="font-semibold">Plan vs actual by project</div>
              <div className="text-[11px] text-[#6a736f]">Across {WEEKS} weeks in view</div>
            </div>
            <div className="mt-3 space-y-3">
              {projects.map((p) => {
                const plannedTotal = weeks.reduce((acc, d) => acc + allocs
                  .filter((x) => x.project_id === p.id && x.week_start === iso(d))
                  .reduce((s, x) => s + x.hours, 0), 0);
                const actualTotal = weeks.reduce((acc, d) => acc + actualForProjectWeek(p.id, iso(d)), 0);
                if (!plannedTotal && !actualTotal) return null;
                const c = clientById[p.client_id];
                const denom = Math.max(plannedTotal, actualTotal, 1);
                const plannedPct = Math.min(100, (plannedTotal / denom) * 100);
                const actualPct = Math.min(100, (actualTotal / denom) * 100);
                const slip = slipColor(actualTotal, plannedTotal);
                return (
                  <div key={p.id} className="text-sm" data-testid={`project-plan-actual-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: c?.color }} />
                        <span>{p.name} <span className="text-[#6a736f]">— {c?.name}</span></span>
                      </div>
                      <div className="tabular-nums text-[#3f4a44] text-xs">
                        <span className="font-semibold">{plannedTotal.toFixed(1)}h</span>
                        <span className="text-[#6a736f]"> planned · </span>
                        <span className="font-semibold" style={{ color: slip || "#3f4a44" }}>{actualTotal.toFixed(1)}h</span>
                        <span className="text-[#6a736f]"> tracked</span>
                      </div>
                    </div>
                    <div className="mt-1.5 space-y-1">
                      <div className="h-1.5 rounded-full bg-cream-2 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${plannedPct}%`, background: (c?.color || "#94A3B8") + "AA" }} />
                      </div>
                      <div className="h-1.5 rounded-full bg-cream-2 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${actualPct}%`, background: slip || (c?.color || "#22C55E") }} />
                      </div>
                    </div>
                  </div>
                );
              })}
              {allocs.length === 0 && entries.length === 0 && (
                <div className="text-sm text-[#6a736f]">No allocations or tracked hours in the visible window yet.</div>
              )}
            </div>
          </div>
        </>
      )}

      {addOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setAddOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={addRow} className="bg-white rounded-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Add allocation row</h2>
              <button type="button" onClick={() => setAddOpen(false)} className="text-[#6a736f]"><X size={18} /></button>
            </div>
            <label className="block text-sm">
              <span className="font-medium">Team member</span>
              <select required value={addForm.user_id} onChange={(e) => setAddForm({ ...addForm, user_id: e.target.value })}
                className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm">
                <option value="">Pick a member…</option>
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="font-medium">Project</span>
              <select required value={addForm.project_id} onChange={(e) => setAddForm({ ...addForm, project_id: e.target.value })}
                className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm">
                <option value="">Pick a project…</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name} — {clientById[p.client_id]?.name || ""}</option>)}
              </select>
            </label>
            <button type="submit" className="btn-coral w-full py-2.5 rounded-md text-sm font-medium">Add row</button>
          </form>
        </div>
      )}

      {capForOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCapForOpen(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveCapacity} className="bg-white rounded-xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Capacity</h2>
              <button type="button" onClick={() => setCapForOpen(null)} className="text-[#6a736f]"><X size={18} /></button>
            </div>
            <p className="text-sm text-[#3f4a44]">Set weekly capacity for <span className="font-medium">{capForOpen.name}</span>. Use 40 for full-time, 32 for a 4-day week, and so on.</p>
            <label className="block text-sm">
              <span className="font-medium">Hours per week</span>
              <input type="number" min={0} max={168} step="any" value={capValue}
                onChange={(e) => setCapValue(e.target.value)}
                className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
            </label>
            <button type="submit" className="btn-coral w-full py-2.5 rounded-md text-sm font-medium">Save capacity</button>
          </form>
        </div>
      )}
    </div>
  );
}

function getWeekNumber(d) {
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = target - firstThursday;
  return 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
}
